import { useEffect, useRef } from 'react'
import { ChatChannelInstance } from '../types'

const UNREAD_BADGE_DISMISS_DELAY = 3000

type UnreadState = {
  first_unread_message_id?: string
  last_read: Date
  last_read_message_id?: string
  unread_messages: number
}

type UseSyncUnreadStateParams = {
  channel: ChatChannelInstance
  unreadState: UnreadState | undefined
  setUnreadState: (state: UnreadState | undefined) => void
}

export function useSyncUnreadState({ channel, unreadState, setUnreadState }: UseSyncUnreadStateParams) {
  const lastChannelId = useRef<string | null>(null)
  const dismissTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearUnreadState = () => {
    if (dismissTimeout.current) {
      clearTimeout(dismissTimeout.current)
      dismissTimeout.current = null
    }
    setUnreadState(undefined)
    lastChannelId.current = null
  }

  useEffect(() => {
    if (!channel) return

    const isSameChannel = lastChannelId.current === channel.id
    if (isSameChannel) return

    lastChannelId.current = channel.id

    const unreadCount = channel.countUnread()
    if (unreadCount <= 0 || unreadState) return

    const userId = channel.getClient().user?.id
    const readState = userId ? channel.state.read[userId] : null

    setUnreadState({
      first_unread_message_id: readState?.first_unread_message_id,
      last_read: readState?.last_read ?? new Date(0),
      last_read_message_id: readState?.last_read_message_id,
      unread_messages: unreadCount,
    })

    dismissTimeout.current = setTimeout(clearUnreadState, UNREAD_BADGE_DISMISS_DELAY)

    return () => {
      if (dismissTimeout.current) {
        clearTimeout(dismissTimeout.current)
        dismissTimeout.current = null
      }
    }
  }, [channel, unreadState])

  useEffect(() => {
    if (!channel) return

    const userId = channel.getClient().user?.id

    const handleRead = (event: any) => {
      if (event?.user?.id !== userId) return
      if (dismissTimeout.current) return
      dismissTimeout.current = setTimeout(clearUnreadState, UNREAD_BADGE_DISMISS_DELAY)
    }

    const listener = channel.on('message.read', handleRead)
    return () => { listener.unsubscribe() }
  }, [channel])
}
